import { type FC, useCallback } from "react";

import type { CardTypeMappingEntity } from "src/app/features/VirtualSiteGroup/types/CardTypeMappingEntity";
import type { ID } from "../../../../core/types/fe/types";
import type { ExtendedColumnsType } from "../../../index";
import { appProfileListSelectConfig } from "../../../SearchForm/FormSearchUtils";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { TableAddNewLineButton } from "../../../Table/components/PropertyTable/TableAddNewLineButton";
import { Actions } from "../../../Table/types/types";
import { type AntCustomFormField, type CommonFieldAttributes } from "../../types/types";
import { SelectDynamic } from "../SelectDynamic/SelectDynamic";
import { TextInput } from "../Text/TextInput";

export interface CardTypesMappingTableProps extends AntCustomFormField<CardTypeMappingEntity[]>, CommonFieldAttributes {}
export const CardTypesMappingTable: FC<CardTypesMappingTableProps> = ({ value = [], onChange, readOnly }) => {
    const appendRowId = (array: CardTypeMappingEntity[]) =>
        array
            .slice()
            .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
            .map((item, idx) => ({ ...item, rowId: idx }));

    const onInput = (cellId: ID | undefined, rowId: ID | undefined, key: keyof CardTypeMappingEntity, newValue: string | number) => {
        const sortedValue = value.slice().sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0));

        const newData = sortedValue.map((row, index) => {
            if (cellId === undefined && row.id === undefined) {
                if (index === Number(rowId)) {
                    return { ...row, [key]: newValue };
                }
                return row;
            }
            if (row.id !== cellId) return row;
            return { ...row, [key]: newValue };
        });
        onDataChanged(newData);
    };

    const getColumnsConfig = (t: Function): ExtendedColumnsType<CardTypeMappingEntity>[] => {
        return [
            {
                title: t("virtualSiteGroup.cardTypeMappings.table.id"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("virtualSiteGroup.cardTypeMappings.table.cardTypeName"),
                key: "cardTypeName",
                dataIndex: "cardTypeName",
                render: (_, record) => (
                    <TextInput
                        name={`cardTypeName-${record.id ?? record.rowId}`}
                        value={record.cardTypeName}
                        readOnly={readOnly || false}
                        onChange={(newValue) => onInput(record.id, record.rowId, "cardTypeName", newValue)}
                    />
                ),
            },
            {
                title: t("virtualSiteGroup.cardTypeMappings.table.applicationProfileListId"),
                key: "applicationProfileListId",
                dataIndex: "applicationProfileListId",
                render: (_, record) => (
                    <SelectDynamic
                        name={`applicationProfileListId-${record.id ?? record.rowId}`}
                        label=""
                        value={record.applicationProfileListId?.toString()}
                        readOnly={readOnly || false}
                        onChange={(newValue) => onInput(record.id, record.rowId, "applicationProfileListId", Number(newValue))}
                        fieldConfig={appProfileListSelectConfig}
                    />
                ),
            },
        ];
    };

    const onDataChanged = useCallback(
        (data: CardTypeMappingEntity[]) => {
            if (onChange) {
                onChange(data);
            }
        },
        [onChange],
    );

    const addNewLine = () => {
        const emptyTableData = {
            id: undefined,
            cardTypeName: "",
            applicationProfileListId: undefined,
        } as CardTypeMappingEntity;
        const newTableValue = [...value, emptyTableData];
        if (onChange) {
            onChange(newTableValue);
        }
    };

    return (
        <PropertyTable<CardTypeMappingEntity>
            name="cardTypesMappings_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="rowId"
            tableData={appendRowId(value) || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            actions={[Actions.DELETE]}
            customActions={[<TableAddNewLineButton key="cardTypeMapping_add-new-line" callback={addNewLine} />]}
        />
    );
};
